import SystemNotify from "@/components/Chat/SystemNotify";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import useFirstModal from "@/components/FirstModal";
import Footer from "@/components/Footer";
import HBImagePreviewModal from "@/components/HBImagePreviewModal";
import HBMessage from "@/components/HBMessage";
import { getModalList } from "@/components/HBModal/ModalWrap";
import Header from "@/components/Header";
import LaunchBottom from "@/components/LaunchBottom";
import accountLocked from "@/components/LoginModal/shared/accountLocked";
import PinDetailModal from "@/components/PinDetail/modal";
import SideButtonList from "@/components/SideButtonList";
import { COOKIE_KEY } from "@/constants/cookieKey";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore, { useInitStore } from "@/context/store";
import { getAds, getGrocery, getMe } from "@/services";
import initTracker from "@/tracker";
import "@/utils/gdSlsRum";
import { isSupportWebp } from "@/utils/imageUrl";
import localStoragePolyfill from "@/utils/localStoragePolyfill";
import { memorizeFn } from "@/utils/memorizeFn";
import { safeParse } from "@/utils/safeParse";
import { setTransferHeaders } from "@/utils/server/headerTransfer";
import { stewardInstance } from "@/utils/steward";
import { ConfigProvider } from "antd";
import Browser from "bowser";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import { useCallback, useEffect, useMemo, useState } from "react";
import smoothscroll from "smoothscroll-polyfill";
const DownloadList = dynamic(
  import("@/components/MaterialDownloadModal/downloadList"),
  { ssr: false }
);
const GlobalUploaderModal = dynamic(
  import("@/components/GlobalUploaderModal"),
  { ssr: false }
);

// 此处必须使用require https://www.npmjs.com/package/next-plugin-antd-less
require("/node_modules/antd/dist/antd.less");
require("../styles/globals.less");
if (typeof window !== "undefined") {
  localStoragePolyfill();
}

const hasFooterPages = [
  { path: "/vip/info", relative: true },
  { path: "/message", relative: false },
  { path: "/login", relative: false },
  { path: "/settings", relative: false },
  { path: "/cooperator/[cooperator]", relative: false },
  { path: "/vip/exchange", relative: false },
  { path: "/activities", relative: true },
  { path: "/activities/start", relative: false },
  { path: "/vip/consult", relative: true },
  { path: "/popular/[urlKey]", relative: true },
  { path: "/coupons/[activityCode]", relative: false },
  { path: "/materials", relative: true },
  { path: "/pages/[[...slug]]", relative: false },
  { path: "/articles", relative: false },
  { path: "/articles/[code]", relative: false },
  { path: "/articles/[code]/[page]", relative: false },
];

const purePages = [
  { path: "/login" },
  { path: "/oauth/authorize" },
  { path: "/signup" },
  { path: "/pay/transit" },
  { path: "/password/reset/[token]" },
  { path: "/browser" },
  { path: "/supernice" },
  { path: "/verify" },
  { path: "/share" },

  {
    path: "/coupons/[activityCode]",
    type: "mobile", // pc、mobile或者不填
  },
  { path: "/vip/consult", type: "mobile" },
  { path: "/vip/exchange", type: "mobile" },
];

// 彩蛋
// console.info(
//   "%c玩花瓣，不如来花瓣：https://s.dancf.com/4c94 ",
//   "color:#ffaac1;"
// );

// 判断是否请求的是html结构
function isRequestingHtml(req) {
  const accept = req?.headers?.accept;
  const urlInfo = req?.url;
  // 需要通过两种方式判断用户请求的是否为html，请求头信息有可能会被修改
  return (
    (accept && accept.indexOf("text/html") !== -1) ||
    (urlInfo &&
      !/\.json$/.test(new URL(urlInfo, `https://${req.headers.host}`).pathname))
  );
}
// 浏览器兼容性检查
const getBrowser = (userAgent) => {
  const result = {
    compatible: true,
    os: "windows",
  };

  if (!userAgent || /spider/i.test(userAgent)) {
    result.compatible = true;
  } else {
    const ua = userAgent?.toLowerCase();
    const browser = Browser.getParser(ua);

    result.os = browser.getOSName(true);
  }

  return result;
};

const getStyle = (pathname, width) => {
  let wrapperStyle = { class: "calculateWrapper" };
  let mainStyle = null;

  if (width) {
    // 如果有 width，设置 width
    wrapperStyle = {
      style: {
        width: width + "px",
      },
      class: "calculateWrapper",
    };
  }

  switch (pathname) {
    // 首页和 pins 页面，不设置 width
    case "/":
    case "/login":
    case "/oauth/authorize":
    case "/about":
    case "/about/goodies":
    case "/about/contact":
    case "/about/disclaimer":
    case "/apps":
    case "/pins/[pinId]":
    case "/guide":
    case "/materials":
    case "/discovery":
    case "/cooperator/hereins":
    case "/muse/register":
    case "/muse/certify":
    case "/cooperator/[cooperator]":
    case "/vip/exchange":
    case "/vip/info":
    case "/pages/[[...slug]]":
    case "/coupons/[activityCode]":
      wrapperStyle = null;
      break;
    case "/activities":
    case "/activities/start":
    case "/vip/consult":
    case "/me/[page]":
    case "/me/[page]/[tab]":
    case "/popular/[urlKey]":
      wrapperStyle = null;
    // eslint-disable-next-line no-fallthrough
    case "/settings":
    case "/message":
      mainStyle = {
        background: "#F7F9FA",
        minHeight: "100vh",
        position: "relative", // 用于把 FooterContent 定位到 main 底部
      };

      break;
    default:
      break;
  }
  return { mainStyle, wrapperStyle };
};

function App(props) {
  const {
    Component,
    pageProps = {},
    os,
    isWeixin,
    isMobile,
    initStore,
    initErr,
  } = props;

  const router = useRouter();
  const pathname = router.pathname;
  const asPath = router.asPath;
  const [dragging, setDragging] = useState(false);

  const env = process.env.NEXT_PUBLIC_DEPLOY_ENV || "prod";
  useInitStore(initStore);
  useFirstModal();
  const {
    wrapperWidth: width,
    config: appConfig,
    user,
    ads,
    setLastBoards,
    dialogs,
  } = useAppStore(
    ({ user, wrapperWidth, config, setLastBoards, ads, dialogs }) => ({
      user,
      wrapperWidth,
      config,

      setLastBoards,
      ads,
      dialogs,
    })
  );
  initTracker({ isMobile, user });

  const isHome = pathname === "/";
  const { hasFooter, isFooterRelative } = useMemo(() => {
    const item = hasFooterPages.find((item) => item.path === pathname);
    return {
      hasFooter: !!item,
      isFooterRelative: item?.relative,
    };
  }, [pathname]);

  const { isPurePage } = useMemo(() => {
    const item = purePages.find((item) => item.path === pathname);
    let isPurePage = !!item;
    if (item?.type === "mobile") {
      isPurePage = isMobile;
    } else if (item?.type === "pc") {
      isPurePage = !isMobile;
    }

    return {
      isPurePage,
    };
  }, [isMobile, pathname]);

  useEffect(() => {
    if (initErr?.msg) {
      // 对此类错误进行特殊处理，账号异常需要重置密码
      if (initErr.msg.includes("重新设置账号密码")) {
        accountLocked(initErr);
        return;
      }

      HBMessage.error(initErr.msg);
    }
  }, [initErr]);

  useEffect(() => {
    // 如果浏览器不支持scroll behavior（比如Safari就不支持），就执行
    const parser = Browser.getParser(window.navigator.userAgent);
    // smoothscroll.polyfill() 自带检测是否支持smoothscroll，来决定是否执行polyfill，
    // 但是其自带的监测方式无法在 safari一些版本中检测出来
    // 所以这里加上根据浏览器版本进行判断是否执行 polyfill
    // 参考链接：https://caniuse.com/scrollintoview
    // https://github.com/iamdustan/smoothscroll/blob/master/src/smoothscroll.js#L10
    const smoothSupport = parser.satisfies({
      safari: "<=15.6",
    });
    if (smoothSupport) {
      // 强制执行polyfill
      window.__forceSmoothScrollPolyfill__ = true;
    }
    smoothscroll.polyfill();
  }, []);

  useEffect(() => {
    document.body.classList.add(`os-${os}`);
    isMobile && document.body.classList.add(`isMobile`);
    isWeixin && document.body.classList.add(`isWeixin`);
    window.GLOBAL_INFO_OS = os; // 写入全局对象上
  }, [isMobile, isWeixin, os]);

  useEffect(() => {
    // 判断是否是内部拖拽
    let isInner = false;
    const onDragEnter = () => {
      const modalList = getModalList();
      const modal = modalList.find((modal) =>
        ["pinUploaderModal", "boardEditModal"].includes(modal.name)
      );
      if (!modal && user && !isInner && pathname !== "/muse/certify") {
        setDragging(true);
      }
    };
    const onMouseLeave = () => {
      // 这个用setTimeout主要为了解决快速操作导致的问题，确保最后才执行，但是safari依然有几率不会触发mouseleave
      setTimeout(() => {
        setDragging(false);
      });
    };
    // 为了解决chorme只要拖动内部元素再从外拖到内就不会触发mouseleave，对safari不生效
    const onDragLeave = (evt) => {
      if (!evt.clientX && !evt.clientY) {
        setDragging(false);
      }
    };
    const onDragStart = () => {
      isInner = true;
    };
    const onDragEnd = () => {
      isInner = false;
    };

    document.documentElement.addEventListener("dragenter", onDragEnter);
    document.documentElement.addEventListener("dragstart", onDragStart);
    document.documentElement.addEventListener("dragend", onDragEnd);
    document.documentElement.addEventListener("mouseleave", onMouseLeave);
    document.documentElement.addEventListener("dragleave", onDragLeave);

    return () => {
      document.documentElement.removeEventListener("dragenter", onDragEnter);
      document.documentElement.removeEventListener("dragstart", onDragStart);
      document.documentElement.removeEventListener("dragend", onDragEnd);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
      document.documentElement.addEventListener("dragleave", onDragLeave);
    };
  }, [user, pathname]);

  useEffect(() => {
    const onStorage = async (evt) => {
      if (evt.key === LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS) {
        const lastBoards = evt.newValue;
        if (lastBoards) {
          try {
            setLastBoards(JSON.parse(lastBoards));
            window.localStorage.removeItem(
              LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS
            );
          } catch (e) {
            console.error("更新最近采集失败");
          }
        }
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [setLastBoards]);

  useEffect(() => {
    if (user) {
      stewardInstance.init();
    }
  }, [user]);

  useEffect(() => {
    // 灰色主题的filter会与fixed冲突，导致页面样式混乱
    // https://juejin.cn/post/6844904117974859783
    if (appConfig?.grayTheme && pathname == "/") {
      document.documentElement.classList.add("theme-gray");
    } else {
      document.documentElement.classList.remove("theme-gray");
    }
  }, [appConfig?.grayTheme, pathname]);
  const { wrapperStyle, mainStyle } = useMemo(() => {
    return getStyle(pathname, width);
  }, [pathname, width]);

  // 页面内容
  const getPageContent = useCallback(() => {
    const { staticPageConfig } = pageProps;

    if (isPurePage || staticPageConfig?.purePage) {
      return <Component {...pageProps} />;
    }

    /* 导航栏 */
    let headerComponent = (
      <Header
        isMobile={isMobile}
        searchWord={pageProps?.searchWord}
        ads={ads}
      />
    );

    /* 页脚 */
    let footerComponent = hasFooter && <Footer relative={isFooterRelative} />;

    // 静态页面
    if (staticPageConfig) {
      if (!staticPageConfig.nav) {
        headerComponent = null;
      }

      if (!staticPageConfig.footer) {
        footerComponent = null;
      }
    }

    return (
      <main className={"main"} style={mainStyle}>
        {/* 导航栏 */}
        {headerComponent}
        {/* 页面内容 */}
        <div
          className={`wrapper ${wrapperStyle?.class}`}
          // style={wrapperStyle.style}
        >
          <Component {...pageProps} />
          <SystemNotify />
        </div>

        {/* 右下角悬浮按钮组 */}
        {!isHome && <SideButtonList />}

        {/* 左下角下载列表 */}
        <DownloadList />

        {/* 采集详情 */}
        <PinDetailModal />

        {/* 全局底部登录栏 */}
        <LaunchBottom
          inlinks={pageProps?.inlinks}
          disabledModalOpenWidth={isHome}
        />

        {/* 拖拽上传 */}
        <GlobalUploaderModal
          visible={dragging}
          onCancel={() => setDragging(false)}
        />

        {/* 页脚 */}
        {footerComponent}
      </main>
    );
  }, [
    ads,
    dragging,
    hasFooter,
    isFooterRelative,
    isHome,
    isMobile,
    isPurePage,
    mainStyle,
    pageProps,
    wrapperStyle,
  ]);

  return (
    <>
      <Head>
        <title>
          花瓣网 -
          陪你做生活的设计师（创意灵感天堂，搜索、发现设计灵感、设计素材）
        </title>

        <meta charSet="utf-8" />
        <meta
          name="keywords"
          content="花瓣网,花瓣,图片大全,设计灵感,创意灵感,素材下载,花瓣网素材图库,矢量图,矢量图库,图片素材,网页素材,免费素材,PS素材,网站素材,设计模板,设计素材,网页模板免费下载,素材,免费设计"
        />
        <meta
          name="description"
          content="花瓣网, 设计师寻找灵感的天堂！图片素材领导者，帮你采集、发现网络上你喜欢的事物。你可以用它收集灵感,保存有用的素材,计划旅行,晒晒自己想要的东西"
        />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta name="renderer" content="webkit" />
        <meta name="copyright" content="huaban.com" />
        <meta name="referrer" content="always" />

        <link rel="apple-touch-icon" href="/img/touch-icon-iphone.png" />
        <link
          rel="apple-touch-icon"
          sizes="76x76"
          href="/img/touch-icon-ipad.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="120x120"
          href="/img/touch-icon-iphone-retina.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="/img/touch-icon-ipad-retina.png"
        />
        <link rel="canonical" href={`https://huaban.com${asPath}`} />
        <meta
          httpEquiv="mobile-agent"
          content={`format=html5;url=https://huaban.com${asPath}`}
        />
      </Head>

      {/* 百度统计脚本 */}
      <Script
        id="baidu-tongji"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            var _hmt = _hmt || [];
            (function() {
              var hm = document.createElement("script");
              hm.src = "https://hm.baidu.com/hm.js?d4a0e7c3cd16eb58a65472f40e7ee543";
              var s = document.getElementsByTagName("script")[0]; 
              s.parentNode.insertBefore(hm, s);
              hm.onload = function(){
                const htmlEl = document.body.parentElement;
                _hmt.push(["_setUserProperty", {"has_plugin": htmlEl.className.indexOf("hbChromeExtInstalled")>=0?1:0}]);
                _hmt.push(["_setUserProperty", {"plugin_version": htmlEl.getAttribute("data-hb-chrome-ext-installed")}]);
              }
            })();
          `,
        }}
      />
      <Script
        src="https://cdn.dancf.com/packages/gaoding/web-canary-client/v1.5.3/index.js"
        strategy="beforeInteractive"
      ></Script>
      {/* 覆盖web-canary-client白名单，避免所有请求带上相关请求头标识 */}
      <Script
        id="GdWebCanaryClient"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            if(window.GdWebCanaryClient && window.GdWebCanaryClient.default){
              window.GdWebCanaryClient.default.setWhiteList(["api${
                env === "prod" ? "" : "-" + env
              }.huaban.com", "https://${
            env === "prod" ? "" : env + "."
          }huaban.com"], true);
        }
          `,
        }}
      ></Script>
      <Script
        src="https://cdn.dancf.com/packages/gaoding/gd-sls-rum/v1.16.0/index.js"
        strategy="beforeInteractive"
      ></Script>

      {/* 没有特殊用处，纯粹为了让插件的截图功能可以使用 */}
      <div id="header"></div>

      <ConfigProvider autoInsertSpaceInButton={false}>
        {getPageContent()}
        <HBImagePreviewModal />
        {dialogs.map((dialog) => dialog)}
      </ConfigProvider>
    </>
  );
}

export default function WrapApp(props) {
  return (
    <ErrorBoundary>
      <App {...props} />
    </ErrorBoundary>
  );
}
const getGlobalConfig = memorizeFn(() =>
  getGrocery("global-config", { serverSide: true }).catch(() => ({}))
);

// getInitialProps会在客户端运行(路由变化时)，需要确保客户端的兼容性
// https://nextjs.org/docs/api-reference/data-fetching/get-initial-props
WrapApp.getInitialProps = async ({ ctx }) => {
  const { req, res } = ctx;
  const userAgent = req?.headers["user-agent"] || "";
  const browserInfo = getBrowser(userAgent);
  const cookie = req?.headers["cookie"];
  const initStore = {};
  let initErr = null;

  // 服务端渲染时，设置transferHeaders,透传部分header
  if (typeof window === "undefined") {
    setTransferHeaders(req?.headers);
    const envInfo = require("@/utils/server/envInfo");
    Object.assign(initStore, { envInfo: envInfo.default });
  }

  isSupportWebp(userAgent);
  const isWeixin = userAgent.toLowerCase().includes("micromessenger");
  const isMobile = getIsMobile(userAgent);

  // 兼容客户端执行情况. 客户端不多次,服务端只有请求html的时候再执行
  if (typeof window === "undefined" && isRequestingHtml(req)) {
    const [config, user, ads] = await Promise.all([
      getGlobalConfig(),
      getMe(cookie),
      getAds({
        positions: [51, 58],
        headers: {
          ...(cookie ? { cookie } : null),
          ...(userAgent ? { "user-agent": userAgent } : null),
        },
      }),
    ]);

    const bannerAd = Array.isArray(ads)
      ? ads.find((ad) => ad?.position?.id === 51)
      : null;
    // const adsData = Array.isArray(ads) ? ads?.pop() : null;
    const initImageConfig = req?.cookies[COOKIE_KEY.IMAGE_URL_CONFIG];

    // 计算宽度
    const pageSetting = safeParse(
      req?.cookies["huaban-page-setting"],
      () => req?.cookies["huaban-page-setting"]
    );
    const itemWidth = 240;
    const itemGap = 24;
    let num = 20;
    if (pageSetting?.columnType === "limit" && pageSetting?.limit) {
      num = pageSetting?.limit;
    }
    useAppStore.setState({ config });
    Object.assign(initStore, {
      config,
      user: user?.err ? null : user,
      userReady: true,
      isMobile,
      ads: ads,
      column: num,
      initImageConfig,
      wrapperWidth: num * (itemWidth + itemGap) - itemGap,
      headerHeight: bannerAd ? 120 : 60,
    });

    // 未登录时返回的是404，不需要处理错误信息
    if (user?.err && user?.err !== 404) {
      initErr = user;
    }

    // 拒绝未登录用户访问
    if (config?.refuseNoLogin && !initStore.user) {
      res.writeHead(307, {
        Location: "/unavailable.html",
      });
      return res.end();
    }

    // 拒绝所有访问
    if (config?.refuse) {
      res.writeHead(307, {
        Location: "/unavailable.html",
      });
      return res.end();
    }
  }
  return {
    os: browserInfo.os,
    isWeixin,
    isMobile,
    initStore,
    ...(initErr ? { initErr } : {}),
  };
};

function getIsMobile(ua, options) {
  options = options || {};
  if (!ua) return false;

  ua = ua.toLowerCase();
  var isIos = !!ua.match(/ip(?:ad|od|hone)/i);
  var isIpad = !!ua.match(/ipad/i);
  var isAndroid = !!ua.match(/(?:android|okhttp)/i);
  var isUcmobile = ua.match(/(ucbrowser|mobile|ucweb)/gi);
  var isWindowsPhone = ua.match(/(Windows Phone)/gi);
  var isMiPad = ua.match(/(MI PAD)/gi);

  return (
    (isIos || isAndroid || isUcmobile || isWindowsPhone) &&
    (!options.excludePad || (!isIpad && !isMiPad))
  );
}
